/* Printora Product V2 — sticky-only Boxraw engine v27, 18 Aug 2026.
   SSR owns structure. JS owns one sticky geometry system only. Native product
   accordions are isolated as a short geometry transaction. When an accordion
   expands while the left gallery is already constrained by the shared product
   bottom, the gallery keeps its exact viewport top until real user scrolling
   reaches the new bottom boundary. */
(() => {
  'use strict';
  if (window.__printoraBoxrawStickyOnlyV27) return;
  window.__printoraBoxrawStickyOnlyV27 = true;

  const DESKTOP = window.matchMedia('(min-width: 1025px)');
  const TOP_GAP = 12;
  const BOTTOM_GAP = 18;
  const HEADER_MOTION_MS = 380;
  const ACCORDION_SETTLE_FRAMES = 4;
  let cleanup = () => {};

  function boot() {
    cleanup();

    const product = document.querySelector('body.template-product .productView');
    if (!product) return;
    const top = product.querySelector('.productView-top');
    const left = product.querySelector('.halo-productView-left');
    const right = product.querySelector('.halo-productView-right, .productView-details');
    if (!left || !right) return;

    const gallery = left.querySelector('[data-pv2-square-gallery], .pv2-square-gallery');
    if (gallery) {
      gallery.hidden = false;
      gallery.removeAttribute('hidden');
      gallery.removeAttribute('aria-hidden');
      product.classList.add('pv2-active', 'pv2-gallery-upgraded', 'pv2-options-ready');
      product.classList.remove('pv2-halo-gallery');
    }

    let lastY = window.scrollY;
    let direction = 'down';
    let raf = 0;
    let ro = null;
    let headerMotionRaf = 0;
    let headerMotionUntil = 0;
    let accordionTopHold = null;
    let leftBoundaryHold = null;
    let accordionTxn = null;
    let accordionSettleRaf = 0;

    function headerHeight() {
      const sticky = document.querySelector('#printora-sticky-header');
      if (sticky) {
        const r = sticky.getBoundingClientRect();
        if (r.height > 0) return Math.max(0, Math.min(r.height, r.bottom));
        return 0;
      }
      const cssValue = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--printora-header-height'));
      return Number.isFinite(cssValue) ? cssValue : 0;
    }

    function setImportant(el, prop, value) {
      el.style.setProperty(prop, value, 'important');
    }

    function clearSticky() {
      left.removeAttribute('data-pv2-sticky');
      right.removeAttribute('data-pv2-boxraw');
      ['position', 'top', 'align-self'].forEach((prop) => left.style.removeProperty(prop));
      ['position', 'top', 'align-self', 'height', 'max-height', 'overflow-anchor'].forEach((prop) => right.style.removeProperty(prop));
      accordionTopHold = null;
      leftBoundaryHold = null;
      accordionTxn = null;
    }

    function openAccordionHeight() {
      let total = 0;
      right.querySelectorAll('.pv2-accordions details[open]').forEach((detail) => {
        const panel = detail.querySelector('.pv2-content');
        if (!panel) return;
        const height = panel.getBoundingClientRect().height;
        if (Number.isFinite(height) && height > 0) total += height;
      });
      return total;
    }

    function moveToward(from, to, amount) {
      if (!Number.isFinite(from)) return to;
      if (!Number.isFinite(to)) return from;
      if (from < to) return Math.min(to, from + amount);
      if (from > to) return Math.max(to, from - amount);
      return to;
    }

    function accordionSummaryFromEvent(event) {
      const target = event.target;
      const summary = target && target.closest ? target.closest('.pv2-accordions summary') : null;
      if (!summary || !right.contains(summary)) return null;
      return summary;
    }

    /* Start BEFORE the native <details> state changes. This is deliberately
       pointerdown/keydown rather than click: by click time a browser can already
       have selected a new scroll anchor on a mid-page sticky layout. */
    function beginAccordionTransaction(summary) {
      if (!summary || !DESKTOP.matches) return;
      const detail = summary.closest('details');
      if (!detail) return;

      if (accordionSettleRaf) {
        cancelAnimationFrame(accordionSettleRaf);
        accordionSettleRaf = 0;
      }

      const rightRect = right.getBoundingClientRect();
      const leftRect = left.getBoundingClientRect();
      const currentTopLimit = Math.max(0, headerHeight() + TOP_GAP);
      const opening = !detail.open;
      const leftIsSticky = left.dataset.pv2Sticky === 'true' || getComputedStyle(left).position === 'sticky';
      const leftIsBottomConstrained = opening && leftIsSticky && leftRect.top < currentTopLimit - 1;

      accordionTxn = {
        detail,
        scrollY: window.scrollY,
        scrollX: window.scrollX,
        rightTop: rightRect.top,
        leftTop: leftRect.top,
        frames: 0
      };

      /* If the gallery is already being pushed above its normal sticky top by
         the shared product bottom, preserve that exact viewport top. Growing
         the right column must not instantly relax that constraint and let the
         gallery drop. Real scrolling is what should move it toward the new
         bottom. */
      if (leftIsBottomConstrained) {
        leftBoundaryHold = {
          top: leftRect.top,
          scrollY: window.scrollY
        };
      } else if (opening) {
        leftBoundaryHold = null;
      }

      /* Arm the existing right-column hold before ResizeObserver can react to
         the expanded height. Nothing is moved here; current geometry is simply
         remembered as the transaction baseline. */
      accordionTopHold = {
        top: rightRect.top,
        scrollY: window.scrollY
      };
    }

    function onAccordionPointerDown(event) {
      const summary = accordionSummaryFromEvent(event);
      if (summary) beginAccordionTransaction(summary);
    }

    function onAccordionKeyDown(event) {
      if (event.repeat || (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar')) return;
      const summary = accordionSummaryFromEvent(event);
      if (summary) beginAccordionTransaction(summary);
    }

    function restoreAccordionViewport(txn) {
      if (!txn || accordionTxn !== txn) return;
      const y = window.scrollY;
      if (Math.abs(y - txn.scrollY) > 0.5) {
        window.scrollTo(txn.scrollX, txn.scrollY);
      }
      lastY = txn.scrollY;
      accordionTopHold = {
        top: txn.rightTop,
        scrollY: txn.scrollY
      };
    }

    function settleAccordionTransaction(txn) {
      if (!txn || accordionTxn !== txn) return;

      restoreAccordionViewport(txn);
      schedule();
      txn.frames += 1;

      /* A few paint frames cover the native details layout + ResizeObserver
         delivery. After that the browser cannot retroactively shift the page.
         Keep both geometry holds alive: normal user scrolling hands each column
         smoothly to its new boundary using the expanded height. */
      if (txn.frames < ACCORDION_SETTLE_FRAMES) {
        accordionSettleRaf = requestAnimationFrame(() => settleAccordionTransaction(txn));
      } else {
        accordionSettleRaf = 0;
        accordionTxn = null;
      }
    }

    function onAccordionToggle(event) {
      const detail = event.target;
      const txn = accordionTxn;
      if (!txn || detail !== txn.detail || !right.contains(detail)) return;

      /* toggle fires after the native open state changed but before the next
         useful paint. Restore immediately, then protect the following frames. */
      restoreAccordionViewport(txn);
      schedule();
      if (!accordionSettleRaf) {
        accordionSettleRaf = requestAnimationFrame(() => settleAccordionTransaction(txn));
      }
    }

    function apply() {
      raf = 0;
      if (!DESKTOP.matches) {
        clearSticky();
        return;
      }

      const viewportH = window.innerHeight;
      const topLimit = Math.max(0, headerHeight() + TOP_GAP);
      const leftH = left.scrollHeight;
      const rightH = right.scrollHeight;
      const rightClosedAccordionBaselineH = Math.max(0, rightH - openAccordionHeight());

      /* Accordion content is a right-column concern. It never changes whether
         the left gallery is sticky or relative. */
      if (rightClosedAccordionBaselineH > leftH + 20) {
        left.dataset.pv2Sticky = 'true';
        setImportant(left, 'position', 'sticky');
        setImportant(left, 'align-self', 'flex-start');

        let requestedLeftTop = topLimit;
        if (leftBoundaryHold) {
          const leftDeltaY = window.scrollY - leftBoundaryHold.scrollY;

          /* Opening/reflow and downward travel keep the exact captured top.
             Scrolling upward is allowed to return toward the normal sticky top,
             but only by the number of pixels the shopper actually scrolls. */
          requestedLeftTop = leftDeltaY < 0
            ? moveToward(leftBoundaryHold.top, topLimit, Math.abs(leftDeltaY))
            : leftBoundaryHold.top;

          if (!accordionTxn && leftDeltaY < 0 && Math.abs(requestedLeftTop - topLimit) <= 1) {
            leftBoundaryHold = null;
            requestedLeftTop = topLimit;
          }
        }

        setImportant(left, 'top', `${Math.round(requestedLeftTop)}px`);

        /* While scrolling down, the hold stays in charge until the NEW shared
           product bottom physically catches the gallery. At that moment CSS
           containment forces the actual top above our requested top. Releasing
           here is invisible because the parent boundary already owns position. */
        if (leftBoundaryHold && !accordionTxn) {
          const leftDeltaY = window.scrollY - leftBoundaryHold.scrollY;
          if (leftDeltaY >= 0) {
            const actualLeftTop = left.getBoundingClientRect().top;
            if (actualLeftTop < requestedLeftTop - 1) {
              leftBoundaryHold = null;
              setImportant(left, 'top', `${Math.round(topLimit)}px`);
            }
          }
        }
      } else {
        leftBoundaryHold = null;
        left.dataset.pv2Sticky = 'false';
        setImportant(left, 'position', 'relative');
        setImportant(left, 'top', 'auto');
        setImportant(left, 'align-self', 'flex-start');
      }

      right.dataset.pv2Boxraw = 'true';
      setImportant(right, 'position', 'sticky');
      setImportant(right, 'align-self', 'flex-start');
      setImportant(right, 'height', 'auto');
      setImportant(right, 'max-height', 'none');
      setImportant(right, 'overflow-anchor', 'none');

      const availableH = viewportH - topLimit - BOTTOM_GAP;

      if (accordionTopHold) {
        const deltaY = window.scrollY - accordionTopHold.scrollY;
        let targetTop = topLimit;

        if (rightH > availableH && deltaY >= 0) {
          targetTop = Math.min(topLimit, viewportH - rightH - BOTTOM_GAP);
        }

        const heldTop = deltaY === 0
          ? accordionTopHold.top
          : moveToward(accordionTopHold.top, targetTop, Math.abs(deltaY));

        setImportant(right, 'top', `${Math.round(heldTop)}px`);

        if (!accordionTxn && Math.abs(deltaY) > 0 && Math.abs(heldTop - targetTop) <= 1) {
          accordionTopHold = null;
        }
        return;
      }

      if (rightH <= availableH) {
        setImportant(right, 'top', `${Math.round(topLimit)}px`);
      } else if (direction === 'down') {
        const bottomTop = viewportH - rightH - BOTTOM_GAP;
        setImportant(right, 'top', `${Math.round(Math.min(topLimit, bottomTop))}px`);
      } else {
        setImportant(right, 'top', `${Math.round(topLimit)}px`);
      }
    }

    function schedule() {
      if (!raf) raf = requestAnimationFrame(apply);
    }

    function trackHeaderMotion() {
      headerMotionUntil = performance.now() + HEADER_MOTION_MS;
      if (headerMotionRaf) return;

      const frame = (now) => {
        schedule();
        if (now < headerMotionUntil) {
          headerMotionRaf = requestAnimationFrame(frame);
        } else {
          headerMotionRaf = 0;
          schedule();
        }
      };
      headerMotionRaf = requestAnimationFrame(frame);
    }

    function onScroll() {
      const y = window.scrollY;

      /* A native details toggle is not user scrolling. Its short transaction
         restores any browser-created scroll delta itself, so do not reinterpret
         that delta as a Boxraw direction change. */
      if (accordionTxn) {
        restoreAccordionViewport(accordionTxn);
        schedule();
        return;
      }

      if (y > lastY) direction = 'down';
      else if (y < lastY) direction = 'up';
      lastY = y;
      schedule();
    }

    function onHeaderVisibility() {
      trackHeaderMotion();
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    DESKTOP.addEventListener?.('change', schedule);
    window.addEventListener('pv2:imagesready', schedule);
    window.addEventListener('printora:gallery-resized', schedule);
    window.addEventListener('printora:header-visibility', onHeaderVisibility);
    document.addEventListener('pv2:garmentchange', schedule);
    right.addEventListener('pointerdown', onAccordionPointerDown, true);
    right.addEventListener('keydown', onAccordionKeyDown, true);
    right.addEventListener('toggle', onAccordionToggle, true);

    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(schedule);
      ro.observe(left);
      ro.observe(right);
    }

    schedule();
    [80, 300, 900].forEach((delay) => setTimeout(schedule, delay));

    cleanup = () => {
      if (raf) cancelAnimationFrame(raf);
      if (headerMotionRaf) cancelAnimationFrame(headerMotionRaf);
      if (accordionSettleRaf) cancelAnimationFrame(accordionSettleRaf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', schedule);
      DESKTOP.removeEventListener?.('change', schedule);
      window.removeEventListener('pv2:imagesready', schedule);
      window.removeEventListener('printora:gallery-resized', schedule);
      window.removeEventListener('printora:header-visibility', onHeaderVisibility);
      document.removeEventListener('pv2:garmentchange', schedule);
      right.removeEventListener('pointerdown', onAccordionPointerDown, true);
      right.removeEventListener('keydown', onAccordionKeyDown, true);
      right.removeEventListener('toggle', onAccordionToggle, true);
      try { ro?.disconnect(); } catch (_) {}
      clearSticky();
    };
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', () => requestAnimationFrame(boot));
})();
